/**
 * CPR Error Handler Utility
 * Handles error categorization and NewRelic logging for CPR API responses
 */

import {
  logCustomAttributeToNewRelicWithAppData,
  logPageActionToNewRelic,
  getEncryptedMobileNumber,
} from "./newRelic-log-utils";

/**
 * CPR error code configuration
 */
interface CprErrorConfig {
  code: number;
  message: string;
  isSuccess: boolean;
}

/**
 * Valid CPR error codes with their messages and success status
 */
const CPR_ERROR_CODES: CprErrorConfig[] = [
  // Success codes
  { code: 3038, message: "SUCCESS", isSuccess: true },
  { code: 1038, message: "SUCCESS", isSuccess: true },
  { code: 3038, message: "Data dumped Successfully on Cosmos", isSuccess: true },
  { code: 3038, message: "SUCCESS|Bureau data uploaded to blob", isSuccess: true },
  { code: 3038, message: "Report Generated Successfully", isSuccess: true },

  // Business/Application failure codes
  { code: 1009, message: "Channel subscription mapping available", isSuccess: false },
  { code: 1002, message: "Request validation Not Successful", isSuccess: false },
  { code: 3012, message: "Invalid csrequestid", isSuccess: false },
  { code: 3630, message: "Data not received from bureau", isSuccess: false },
  { code: 3782, message: "BlobId not received", isSuccess: false },
  { code: 3914, message: "Invalid customer type, journey not allowed", isSuccess: false },
  { code: 1009, message: "Channelid and Channelname mapping not available", isSuccess: false },
  { code: 1050, message: "PAN validation fail", isSuccess: false },
  { code: 3012, message: "Age validation failed", isSuccess: false },
  { code: 3012, message: "Customer name validation failed", isSuccess: false },
  { code: 3012, message: "Pincode validation failed", isSuccess: false },
  { code: 3926, message: "FPR is already generated", isSuccess: false },
  { code: 3623, message: "FPR report generation failed, will reflow", isSuccess: false },
  { code: 3624, message: "FPR report generation is in progress", isSuccess: false },
  {
    code: 3625,
    message: "FPR report generation failed as no data found in D2C API",
    isSuccess: false,
  },
  { code: 3016, message: "Failed to push data on Cosmos", isSuccess: false },
  { code: 1005, message: "Channel not registered", isSuccess: false },
  { code: 1006, message: "Subscription not registered", isSuccess: false },
  { code: 1010, message: "Channel is NOT mapped with the subscription", isSuccess: false },
  { code: 3080, message: "Cross Sell Request validation Not Successful", isSuccess: false },
  { code: 3333, message: "Payment is already completed", isSuccess: false },
  { code: 3921, message: "Payment Status Pending", isSuccess: false },
  { code: 3190, message: "Wrong payment status :", isSuccess: false },
  {
    code: 3191,
    message:
      "Payment amount Rs <amount> is not equal to productfees Rs <amount> for selected product",
    isSuccess: false,
  },
  { code: 3017, message: "Blob upload API failed", isSuccess: false },
  { code: 3017, message: "Blob upload API failed, Document blob ID missing", isSuccess: false },
  { code: 3046, message: "Invalid Product name", isSuccess: false },
  { code: 3016, message: "Data Science Report Failed", isSuccess: false },
  { code: 1049, message: "Mobile validation fail", isSuccess: false },
  { code: 3631, message: "Data not received from D2C", isSuccess: false },
];

/**
 * System error HTTP status codes
 */
const SYSTEM_ERROR_CODES = [500, 403, 401, 400, 502, 503, 504, 408];

/**
 * Get all valid CPR error codes
 */
export const getValidCprErrorCodes = (): number[] => {
  return [...new Set(CPR_ERROR_CODES.map((error) => error.code))];
};

/**
 * Check if a given code is a valid CPR error code
 */
export const isValidCprErrorCode = (code: number): boolean => {
  return CPR_ERROR_CODES.some((error) => error.code === code);
};

/**
 * Check if a given HTTP status code is a system error
 */
export const isSystemErrorCode = (statusCode: number): boolean => {
  return SYSTEM_ERROR_CODES.includes(statusCode);
};

/**
 * Get error configuration by code
 */
export const getCprErrorConfig = (code: number): CprErrorConfig | undefined => {
  return CPR_ERROR_CODES.find((error) => error.code === code);
};

/**
 * Error categorization result
 */
interface ErrorCategory {
  type: "valid_error" | "system_error" | "unknown_error";
  code: number;
  message: string;
  isSuccess: boolean;
}

/**
 * Categorize error based on code and HTTP status
 */
export const categorizeError = (
  errorCode: number | undefined,
  httpStatus: number | undefined,
  errorMessage?: string
): ErrorCategory => {
  // Coerce numeric errorCode to avoid type issues
  const numericErrorCode =
    errorCode !== undefined && !Number.isNaN(Number(errorCode)) ? Number(errorCode) : undefined;

  // Prioritise CPR business error codes over HTTP/system errors
  if (numericErrorCode !== undefined && isValidCprErrorCode(numericErrorCode)) {
    const config = getCprErrorConfig(numericErrorCode);
    return {
      type: "valid_error",
      code: numericErrorCode,
      message: errorMessage || config?.message || "Unknown error",
      isSuccess: config?.isSuccess ?? false,
    };
  }

  // Check if it's a system error (HTTP status)
  if (httpStatus !== undefined && isSystemErrorCode(httpStatus)) {
    return {
      type: "system_error",
      code: httpStatus,
      message: errorMessage || `System error: ${httpStatus}`,
      isSuccess: false,
    };
  }

  // Unknown error
  return {
    type: "unknown_error",
    code: errorCode || httpStatus || 0,
    message: errorMessage || "Unknown error",
    isSuccess: false,
  };
};

/**
 * Log CPR error to NewRelic with proper categorization
 */
export const logCprErrorToNewRelic = (
  errorCode: number | undefined,
  httpStatus: number | undefined,
  errorMessage?: string,
  additionalData?: Record<string, string | number | boolean | null>
): void => {
  const category = categorizeError(errorCode, httpStatus, errorMessage);

  const errorData: Record<string, string | null> = {
    errorType: category.type,
    errorCode: String(category.code),
    httpStatus: httpStatus ? String(httpStatus) : null,
    message: category.message,
    isSuccess: String(category.isSuccess),
    apiName: (additionalData?.apiName as string) || null,
    encrypted_mobile: null,
  };

  // Log immediately (synchronously) to avoid losing events on navigation
  let canonicalEventName: string;
  let usePageAction = false;

  if (category.type === "system_error") {
    canonicalEventName = "SYSTEM_ERROR_PAGE";
  } else if (category.type === "valid_error") {
    if (category.isSuccess) {
      canonicalEventName = "CPR_SUCCESS";
      usePageAction = true;
      logPageActionToNewRelic(canonicalEventName, {
        ...errorData,
        ...additionalData,
      });
    } else {
      canonicalEventName = "VALID_ERROR_PAGE";
    }
  } else {
    canonicalEventName = "SYSTEM_ERROR_PAGE";
  }

  if (!usePageAction) {
    logCustomAttributeToNewRelicWithAppData(canonicalEventName, errorData);
  }

  // Enrich with mobile number asynchronously (best-effort supplement)
  void getEncryptedMobileNumber()
    .then((encryptedMobile) => {
      if (!encryptedMobile) return;

      const enriched = {
        ...errorData,
        encrypted_mobile: encryptedMobile,
      };

      try {
        if (usePageAction) {
          logPageActionToNewRelic(canonicalEventName, {
            ...enriched,
            ...additionalData,
          });
        } else {
          logCustomAttributeToNewRelicWithAppData(canonicalEventName, enriched);
        }
      } catch (e) {
        // best-effort: don't let enrichment throw
      }
    })
    .catch(() => {
      // best-effort: swallow any errors during enrichment
    });
};

/**
 * Parse CPR API response and log to NewRelic
 */
export interface CprApiResponse {
  statusCode?: number;
  message?: string;
  httpStatus?: number;
  [key: string]: unknown;
}

export const handleCprApiResponse = (
  response: CprApiResponse,
  additionalData?: Record<string, string | number | boolean | null>
): ErrorCategory => {
  const category = categorizeError(response.statusCode, response.httpStatus, response.message);

  logCprErrorToNewRelic(response.statusCode, response.httpStatus, response.message, {
    ...additionalData,
    apiResponse: JSON.stringify(response),
  });

  return category;
};
